import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import type { RootState } from "@/app/store/store";
import { getResultStatus, shouldReportSlowOperation } from "@/app/api/slowOperation";
import { captureSlowOperation } from "@/app/sentry";
import { clearCredentials, setCredentials } from "@/features/auth/slice";

/**
 * Base RTK Query API.
 *
 * - In development the API base URL falls back to `/api/v2` which is
 *   intercepted by MSW when `VITE_ENABLE_MSW=true`.
 * - In production it points at VITE_API_BASE_URL (set as a Heroku config var).
 *
 * Endpoints are injected from feature slices via `baseApi.injectEndpoints()`
 * so we keep this file dependency-light and avoid circular imports.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v2";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth?.accessToken;
    const websiteId = state.auth?.websiteId;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (websiteId) {
      headers.set("X-Website-Id", websiteId);
    }
    return headers;
  },
});

function requestSummary(args: unknown) {
  if (typeof args === "string") return { url: args, method: "GET" };
  if (args && typeof args === "object" && "url" in args) {
    const request = args as { url?: unknown; method?: unknown };
    return {
      url: typeof request.url === "string" ? request.url : "unknown",
      method: typeof request.method === "string" ? request.method : "GET",
    };
  }
  return { url: "unknown", method: "GET" };
}

const baseQueryWithTiming: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const start = Date.now();
  const result = await rawBaseQuery(args, api, extraOptions);
  const durationMs = Date.now() - start;
  const thresholdMs = Number(import.meta.env.VITE_SENTRY_SLOW_API_THRESHOLD_MS ?? "3000");
  // Only report operations that actually completed an HTTP round-trip. A
  // transport-level failure (FETCH_ERROR/TIMEOUT_ERROR) measures time-to-failure,
  // not latency, so reporting it as a (critical) slow operation is misleading
  // noise — see slowOperation.ts / ZENYT-PLATFORM-FRONTEND-STAGING-3.
  if (shouldReportSlowOperation(durationMs, thresholdMs, result, api.endpoint)) {
    const request = requestSummary(args);
    captureSlowOperation("platform_frontend_api_call", durationMs, thresholdMs, {
      endpoint: api.endpoint,
      url: request.url,
      method: request.method,
      status: getResultStatus(result),
    });
  }
  return result;
};

// --------------------------------------------------------------------------- //
// Silent token refresh (baseQueryWithReauth)                                  //
// --------------------------------------------------------------------------- //
//
// The access token embeds `authorized_website_ids` at issue time. If a user is
// granted access to a website AFTER their token was issued, every domain
// endpoint (which checks the website against that claim) returns 403
// "Not authorized for this website" — while `/users/me` and `/websites` (list)
// use token-only auth and keep succeeding, so the app never routes to /login.
// The result is a dead 403 storm with no recovery and a broken render.
//
// On a 401 (invalid/expired token) or that specific 403, hit `GET /refresh`
// (HttpOnly cookie auth) once to re-issue a token with current claims, store
// it, and retry the original request a single time. Concurrent failing
// requests share one in-flight refresh. If refresh fails or the retry still
// 403s, clear credentials so RequireAuth routes to /login.

const AUTH_PATH_PREFIXES = [
  "/signin",
  "/refresh",
  "/signout",
  "/signup",
  "/request-password-reset",
  "/reset-password",
  "/resend-verification",
  "/verify-email",
  "/accept-invite",
  "/saml/discover",
  "/saml/login",
  "/google",
  "/microsoft",
];

function isAuthRequest(args: unknown): boolean {
  const { url } = requestSummary(args);
  if (url === "unknown") return false;
  const path = url.split("?")[0] ?? url;
  return AUTH_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`) || path === `/api/v2${p}`
  );
}

function extractErrorDetail(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  const data = (error as { data?: unknown }).data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return "";
}

function errorStatus(error: unknown): number | string | null {
  if (!error || typeof error !== "object") return null;
  const status = (error as { status?: unknown }).status;
  if (typeof status === "number" || typeof status === "string") return status;
  return null;
}

async function fetchFreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/refresh`, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { access_token?: string } | null;
    return data?.access_token ?? null;
  } catch {
    return null;
  }
}

// Single-flight: all concurrent reauth attempts await the same refresh.
let refreshInFlight: Promise<string | null> | null = null;
function getSingleFlightRefresh(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = fetchFreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

const baseQueryWithReauth: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQueryWithTiming(args, api, extraOptions);

  if (result.error && !isAuthRequest(args)) {
    const status = errorStatus(result.error);
    const detail = extractErrorDetail(result.error);
    const needsReauth =
      status === 401 ||
      (status === 403 && /not authorized for this website/i.test(detail));

    if (needsReauth) {
      const freshToken = await getSingleFlightRefresh();
      if (freshToken) {
        // Keep the active websiteId; just rotate the access token. prepareHeaders
        // reads live Redux state, so the retry picks up the new token.
        api.dispatch(setCredentials({ accessToken: freshToken }));
        return baseQueryWithTiming(args, api, extraOptions);
      }
      // Refresh failed — force a clean re-login instead of the dead 404 state.
      api.dispatch(clearCredentials());
      // `baseApi` is defined below; referenced lazily so this resolves at call time.
      void baseApi.util.resetApiState();
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Finding",
    "FindingCategory",
    "FindingGroup",
    "Agent",
    "ActivityRun",
    "ActivityPill",
    "ActivityMetric",
    "User",
    "Website",
    "SavedScope",
    "SiteStructure",
    "SSOConfig",
  ],
  endpoints: () => ({}),
});
